import React, { useState } from 'react';
import pt from 'prop-types';
import { combine } from '/src/helpers/styles';
import Container from '../../../modules/container';
import Headline, { headlinePropTypes } from '../../ui/headline';
import Link, { linkProptypes } from '../../ui/link';
import { imageProptypes } from '../../ui/image';
import Text from '../../ui/text';
import RandomCompanies from './components/randomCompanies';
import RandomProjects from './components/randomProjects';
import SpecificProjects from './components/specificProjects';
import SpecificCompanies from './components/specificCompanies';
import SpecificCollections from './components/specificCollections';
import SpecificJurors from './components/specificJurors';
import RandomJurors from './components/randomJurors';
import * as styles from './styles.module.scss';
import ContinentRankings from './components/continentRankings';
import InfiniteJurors from './components/infiniteJurors';
import InfiniteCompanies from './components/infiniteCompanies';
import InfiniteProjects from './components/infiniteProjects';

const DynamicTeaser = ({
  className,
  headline,
  type,
  amountOfRandomTeaser,
  limit,
  queryParameters,
  dataIds,
  beigeBackground,
  firstItemHeadline,
  link,
  firstItemText,
  firstItemLink,
  totalAmountLabel,
  collectionImages,
}) => {
  const [total, setTotal] = useState(null);
  const profileTypeId = link?.queryParameter?.split('=')?.[1];

  return (
    <Container
      fullWidth
      className={combine(
        styles.wrapper,
        beigeBackground && styles.bgBeige,
        className
      )}
    >
      <Container
        className={styles.relative}
        fullWidth={type === 'Best Companies By Continent'}
      >
        <div className={styles.hlWrapper}>
          {headline && (
            <Headline
              text={headline}
              level="h2"
              type="h1"
              className={styles.hl}
            />
          )}
          {total && totalAmountLabel && (
            <Text as="span" type="metanavi" variant="semibold">
              {total} {totalAmountLabel}
            </Text>
          )}
        </div>

        {/* Projects */}
        {type === 'Random Projects Slider' && (
          <RandomProjects
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
          />
        )}
        {type === 'Random Projects Grid' && (
          <RandomProjects
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            layout="grid"
          />
        )}
        {type === 'Specific Projects Grid' && dataIds && (
          <SpecificProjects dataIds={dataIds} layout="grid" />
        )}
        {type === 'Specific Projects Slider' && dataIds && (
          <SpecificProjects
            dataIds={dataIds}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
          />
        )}

        {/* Companies */}
        {type === 'Specific Companies Slider' && dataIds && (
          <SpecificCompanies
            dataIds={dataIds}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
          />
        )}
        {type === 'Specific Companies Grid' && dataIds && (
          <SpecificCompanies dataIds={dataIds} layout="grid" />
        )}
        {type === 'Random Companies Slider' && (
          <RandomCompanies
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
            profileTypeId={profileTypeId}
          />
        )}
        {type === 'Random Companies Grid' && (
          <RandomCompanies
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            layout="grid"
          />
        )}
        {type === 'Random Companies Creatives Slider' && (
          <RandomCompanies
            isCreative
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
          />
        )}
        {type === 'Random Companies Creatives Grid' && (
          <RandomCompanies
            isCreative
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            layout="grid"
          />
        )}

        {/* Collections */}
        {type === 'Specific Collection Slider' && dataIds && (
          <SpecificCollections
            dataIds={dataIds}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
            collectionImages={collectionImages}
          />
        )}

        {/* Jurors */}
        {type === 'Specific Jurors Grid' && dataIds && (
          <SpecificJurors dataIds={dataIds} layout="grid" />
        )}
        {type === 'All Jurors 2022' && (
          <InfiniteJurors setTotal={setTotal} filtersAsQueryString="yearId=0" />
        )}
        {type === 'Talent Jurors 2022' && (
          <InfiniteJurors
            setTotal={setTotal}
            filtersAsQueryString={'yearId=0&awardId=1'}
          />
        )}
        {type === 'All SIP 2021' && (
          <InfiniteProjects
            filtersAsQueryString={'awardId=8888&sort=desc&yearId=1'}
            setTotal={setTotal}
          />
        )}
        {type === 'Infinite Jurors' && (
          <InfiniteJurors
            setTotal={setTotal}
            filtersAsQueryString={queryParameters || ''}
            limit={limit}
          />
        )}
        {type === 'Infinite Companies' && (
          <InfiniteCompanies
            setTotal={setTotal}
            filtersAsQueryString={queryParameters || ''}
            limit={limit}
          />
        )}
        {type === 'Infinite Projects' && (
          <InfiniteProjects
            setTotal={setTotal}
            filtersAsQueryString={queryParameters || ''}
            limit={limit}
          />
        )}

        {type === 'Random Jurors Slider' && (
          <RandomJurors
            setTotal={setTotal}
            amountOfRandomTeaser={amountOfRandomTeaser}
            firstItemHeadline={firstItemHeadline}
            firstItemText={firstItemText}
            firstItemLink={firstItemLink}
          />
        )}

        {/* Best Companies by continent */}
        {type === 'Best Companies By Continent' && <ContinentRankings />}

        {link && (
          <div className={styles.linkWrapper}>
            <Link link={link} asButton btnType="primary">
              {link.text}
            </Link>
          </div>
        )}
      </Container>
    </Container>
  );
};

DynamicTeaser.propTypes = {
  headline: pt.shape(headlinePropTypes),
  type: pt.string.isRequired,
  link: pt.shape(linkProptypes),
  dataIds: pt.arrayOf(pt.string),
  amountOfRandomTeaser: pt.number,
  beigeBackground: pt.bool,
  firstItemHeadline: pt.shape(headlinePropTypes),
  firstItemText: pt.string,
  firstItemLink: pt.shape(linkProptypes),
  totalAmountLabel: pt.string,
  collectionImages: pt.arrayOf(pt.shape(imageProptypes)),
  className: pt.string,
};

DynamicTeaser.defaultProps = {
  headline: null,
  link: null,
  dataIds: [],
  amountOfRandomTeaser: 3,
  beigeBackground: false,
  firstItemHeadline: null,
  firstItemText: '',
  firstItemLink: null,
  totalAmountLabel: '',
  collectionImages: [],
  className: '',
};

export default DynamicTeaser;
